import { ChangeEvent, MouseEvent, useCallback, useLayoutEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, ExpandMore, Settings } from "@mui/icons-material";
import {
  Box,
  Button,
  buttonClasses,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  inputClasses,
  List,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  outlinedInputClasses,
  Paper,
  Popper,
  Stack,
  styled,
  Typography as T,
  Tooltip,
  useTheme,
} from "@mui/material";
import SourceFileConfigurationModal from "datasets/pages/tables/components/SourceFileConfigurationModal";
import FileValidationModal from "datasets/pages/tables/components/file-validation/FileValidationModal";
import { useIngestionHydration } from "datasets/pages/tables/components/file-validation/hooks/useIngestionHydration";
import useIngestContext from "datasets/pages/tables/contexts/ingest";
import useSnowflakeSchemas from "datasets/pages/tables/hooks/useSnowflakeSchemas";
import { Ingest } from "datasets/pages/tables/types/interfaces/ingest";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { SearchIcon } from "~/assets/icons";
import TextOverflow from "~/components/TextOverflow";
import { ValidationMetadata } from "./file-validation/types";

export const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  gap: 4,
  padding: 8,
  [`.${inputClasses.inputSizeSmall}`]: {
    height: "unset",
    lineHeight: "20px",
    padding: 0,
  },
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: theme.palette.grey[300],
  },
}));

export const StyledLoadingButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.outlinedPrimary}`]: {
    borderColor: theme.palette.grey[300],
    justifyContent: "space-between",
    lineHeight: "20px",
    padding: 8,
    "&:hover": {
      backgroundColor: "#FFFFFF",
      borderColor: theme.palette.primary.dark,
    },
  },
  [`& .${buttonClasses.endIcon}`]: {
    color: theme.palette.text.primary,
  },
  [`& .${buttonClasses.loadingIndicator}`]: {
    color: theme.palette.primary.dark,
  },
}));

const FileMetadataSuccessText = () => {
  const { watch } = useFormContext<Ingest>();
  const sourceFileKey = watch("platform_storage_key");

  const sourceMetadataKey = watch("metadata_platform_storage_key");

  if (!sourceMetadataKey) {
    return null;
  }

  const metadataFileIdx = sourceMetadataKey.lastIndexOf("/");
  const metadataKeyFromSourceFile = `${sourceFileKey}.metadata.json`;
  const hasFoundMetadataFileFromSourceFile = sourceMetadataKey === metadataKeyFromSourceFile;

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <CheckCircle sx={{ width: 16, height: 16, color: "success.main" }} />
      <T variant="caption" sx={{ opacity: 0.6 }}>
        {`${
          hasFoundMetadataFileFromSourceFile ? "Found" : "Using"
        } metadata file: "${sourceMetadataKey.slice(metadataFileIdx + 1)}"`}
      </T>
    </Box>
  );
};

const SourceSelect = () => {
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [fileValidationModalOpen, setFileValidationModalOpen] = useState(false);
  const { setValue, watch, getValues } = useFormContext<Ingest>();
  const { setDataProfile, isFileConfirmed, setIsFileConfirmed } = useIngestContext();
  const sourceFileKey = watch("platform_storage_key");
  const sourceMetadataKey = watch("metadata_platform_storage_key");

  // URL params for validation state persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const ingestionId = searchParams.get("ingestion");

  // SWR deduplicates — same key as IngestForm's call, no extra fetch
  const { isHydrating } = useIngestionHydration();

  const getSourceFileLabel = useCallback(() => {
    if (isHydrating) {
      return null;
    }
    if (isFileConfirmed && sourceFileKey) {
      return resolveRouterPath(sourceFileKey);
    }
    if (ingestionId) {
      return "Continue validation...";
    }
    return "Select file...";
  }, [ingestionId, isFileConfirmed, isHydrating, sourceFileKey]);

  /**
   * Set validated=true in URL and mark file as confirmed in context.
   * Called from the modal's onConfirm (Confirm & Submit).
   */
  const handleValidationConfirmed = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("validated", "true");
    setSearchParams(newParams, { replace: true });
    setIsFileConfirmed(true);
  }, [searchParams, setSearchParams, setIsFileConfirmed]);

  const handleFileSelectClick = () => {
    setFileValidationModalOpen(true);
  };

  /**
   * Called when user clicks "Confirm & Submit" in the FileValidationModal.
   * Integration point between file validation and the ingest form.
   */
  const handleFileValidated = (file: FileInfo, validationMetadata: ValidationMetadata) => {
    const sourceUri = file.s3_uri || file.key;

    // Set the source_file_uri in the form
    setValue("source_file_uri", sourceUri);
    setValue("platform_storage_key", file.key);
    setValue("file_size", file.size);

    // Set file_format if available from validation job
    if (validationMetadata?.file_format) {
      setValue("metadata_config.file_format", validationMetadata.file_format);
    }

    // Set columns if available from validation job
    if (validationMetadata?.dataProfile?.columns) {
      const profileColumns = validationMetadata.dataProfile.columns.map((col) => ({
        name: col.name,
        type: col.type,
        description: "",
        tags: [],
      }));
      setValue("metadata_config.columns", profileColumns);
    }

    if (validationMetadata?.dataProfile) {
      setDataProfile(validationMetadata.dataProfile);
    }
    setFileValidationModalOpen(false);
  };

  const sourceFileLabel = getSourceFileLabel();

  return (
    <Stack alignItems="flex-start" gap={2} width="100%">
      <Stack gap={0.5} width="100%">
        <T variant="body2" fontWeight={500}>
          Source File
        </T>
        <Box alignItems="center" display="flex" gap={1}>
          <Box sx={{ flex: 1, position: "relative" }}>
            <StyledLoadingButton
              fullWidth
              size="small"
              onClick={handleFileSelectClick}
              variant="outlined"
            >
              {isHydrating ? (
                <Box display="flex" alignItems="center" gap={1} sx={{ opacity: 0.6 }}>
                  <CircularProgress size={14} />
                  <T variant="body2">Loading file...</T>
                </Box>
              ) : (
                <TextOverflow
                  variant="body2"
                  maxWidth={560}
                  sx={{
                    opacity: isFileConfirmed && sourceFileKey ? 1 : 0.6,
                    textAlign: "left",
                  }}
                >
                  {sourceFileLabel}
                </TextOverflow>
              )}
            </StyledLoadingButton>
          </Box>
          <Tooltip arrow title="Edit configuration" enterDelay={500}>
            <span>
              <IconButton
                disabled={!sourceFileKey}
                sx={{
                  height: 39,
                  width: 39,
                }}
                onClick={() => setMetadataModalOpen(true)}
              >
                <Settings />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <FileMetadataSuccessText />
      </Stack>
      {metadataModalOpen && (
        <SourceFileConfigurationModal
          open={metadataModalOpen}
          onClose={() => setMetadataModalOpen(false)}
          metadataConfig={getValues("metadata_config")}
          setIngestFormValue={setValue}
          metadataFileName={sourceMetadataKey}
        />
      )}
      {fileValidationModalOpen && (
        <FileValidationModal
          open={fileValidationModalOpen}
          onClose={() => setFileValidationModalOpen(false)}
          onFileValidated={handleFileValidated}
          onConfirm={handleValidationConfirmed}
        />
      )}
    </Stack>
  );
};

const SCHEMA_POPPER_CHROME_HEIGHT = 148;
const MIN_SCHEMA_LIST_HEIGHT = 100;

const SchemaSelect = () => {
  const theme = useTheme();
  const fieldKey = "metadata_config.database_schema";
  const { watch, setValue } = useFormContext<Ingest>();
  const value = watch(fieldKey);
  const { data: schemas, isLoading } = useSnowflakeSchemas();
  const [popperAnchorEl, setPopperAnchorEl] = useState<null | HTMLElement>(null);
  const [schemaSearchTerm, setSchemaSearchTerm] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  // 290 is the functional fallback on first render only — useLayoutEffect immediately overrides it
  const [dynamicListHeight, setDynamicListHeight] = useState<number>(290);

  useLayoutEffect(() => {
    if (popperAnchorEl) {
      const calculateHeight = () => {
        const anchorRect = popperAnchorEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const available = viewportHeight - anchorRect.bottom - SCHEMA_POPPER_CHROME_HEIGHT;
        setDynamicListHeight(Math.max(MIN_SCHEMA_LIST_HEIGHT, Math.min(available, 290)));
      };

      calculateHeight();
      window.addEventListener("resize", calculateHeight);
      return () => window.removeEventListener("resize", calculateHeight);
    }
  }, [popperAnchorEl]);

  const filteredSchemas = schemas?.filter((schema) =>
    schema.name.toLowerCase().includes(schemaSearchTerm.toLowerCase())
  );

  const handleSchemasOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setPopperAnchorEl(event.currentTarget);
  };

  const handleSchemasClose = () => {
    setPopperAnchorEl(null);
    setSchemaSearchTerm("");
  };

  return (
    <Stack gap={0.5} width="100%">
      <T variant="body2" fontWeight={500}>
        Destination
      </T>
      <StyledLoadingButton
        size="small"
        endIcon={<ExpandMore />}
        loading={isLoading}
        loadingPosition="end"
        onClick={handleSchemasOpen}
        sx={{ justifyContent: "space-between" }}
        variant="outlined"
      >
        <T fontSize="inherit" sx={{ opacity: value ? 1 : 0.5 }}>
          {value ? value : "Select destination..."}
        </T>
      </StyledLoadingButton>
      <T variant="caption" sx={{ opacity: 0.5 }}>
        Imported data will be stored as a new table within the selected destination.
      </T>
      <Popper
        disablePortal
        anchorEl={popperAnchorEl}
        open={!!popperAnchorEl}
        placement="bottom-start"
        sx={{ width: "100%", zIndex: theme.zIndex.tooltip }}
      >
        <ClickAwayListener onClickAway={handleSchemasClose}>
          <Paper
            sx={{
              boxShadow:
                "0px 1px 5px 0px #0000001F, 0px 2px 2px 0px #00000024, 0px 3px 1px -2px #00000033",
            }}
          >
            <Stack alignItems="flex-end" p={2} gap={1.25}>
              <StyledOutlinedInput
                fullWidth
                autoFocus
                placeholder="Search..."
                size="small"
                startAdornment={<SearchIcon width={16} />}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSchemaSearchTerm(e.target.value)}
              />
              <Stack
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1.5,
                  width: "100%",
                }}
              >
                <List dense disablePadding sx={{ height: dynamicListHeight, overflow: "auto" }}>
                  {filteredSchemas?.map((schema, i) => (
                    <ListItemButton
                      key={i}
                      selected={selectedSchema === schema.name}
                      onClick={() => setSelectedSchema(schema.name)}
                    >
                      <ListItemText>{schema.name}</ListItemText>
                    </ListItemButton>
                  ))}
                </List>
              </Stack>
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={handleSchemasClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setValue(fieldKey, selectedSchema);
                    handleSchemasClose();
                  }}
                >
                  Select
                </Button>
              </Box>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
};

const ImportDetails = ({ onNextPanel }: { onNextPanel: () => void }) => {
  const { watch } = useFormContext<Ingest>();
  const { isFileConfirmed } = useIngestContext();
  const storageKey = watch("platform_storage_key");
  const destination = watch("metadata_config.database_schema");

  return (
    <>
      <SourceSelect />
      {isFileConfirmed && <SchemaSelect />}

      <Button
        variant="contained"
        disabled={!(storageKey && destination && isFileConfirmed)}
        onClick={() => onNextPanel()}
      >
        Next
      </Button>
    </>
  );
};

export default ImportDetails;
